import React from "react";
import type { Budget } from "../../../types";
import { Card } from "../../shared";
import { Icons } from "../../shared/Icons";
import { formatDateShort } from "../../../utils/dateUtils";
import {
  formatBudgetCurrency,
  getBudgetServices,
  getBudgetStatusClasses,
  getBudgetStatusLabel,
  normalizeBudgetSubServices,
  resolveBudgetTotal,
} from "../../../utils/budgetUtils";
import {
  getWhatsAppButtonStyles,
  getWhatsAppIconStyles,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";

interface BudgetsListViewProps {
  budgets: Budget[];
  onEdit: (budget: Budget) => void;
  onCancel: (budget: Budget) => void;
  onComplete: (budget: Budget) => void;
  onDownloadPdf: (budget: Budget) => void;
  onWhatsApp: (budget: Budget) => void;
}

export const BudgetsListView: React.FC<BudgetsListViewProps> = ({
  budgets,
  onEdit,
  onCancel,
  onComplete,
  onDownloadPdf,
  onWhatsApp,
}) => {
  return (
    <div className="space-y-3">
      {budgets.map((budget) => {
        const services = getBudgetServices(budget);
        const total = resolveBudgetTotal(budget);
        const isCreated = budget.status === "Created";
        const clientName = budget.client?.name ?? `Cliente #${budget.clientId}`;
        const hasPhone = Boolean(budget.client?.phoneNumber?.trim());
        const dateLabel = formatDateShort(budget.createdAt.split("T")[0]);
        const carLabel = budget.clientCar
          ? `${budget.clientCar.model} · ${budget.clientCar.plate}`
          : null;

        return (
          <Card
            key={budget.id}
            className="p-4 sm:p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900">
                    #{budget.id} · {clientName}
                  </h3>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${getBudgetStatusClasses(budget.status)}`}
                  >
                    {getBudgetStatusLabel(budget.status)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {dateLabel} · {services.length}{" "}
                  {services.length === 1 ? "serviço" : "serviços"}
                  {carLabel ? ` · ${carLabel}` : ""}
                </p>
                <p className="text-lg font-bold text-sky-700 mt-2">
                  {formatBudgetCurrency(total)}
                </p>
                <ul className="mt-2 text-sm text-gray-600 space-y-0.5">
                  {services.slice(0, 3).map((line) => {
                    const subs = normalizeBudgetSubServices(line);
                    return (
                      <li key={`${budget.id}-${line.serviceId}`}>
                        <span className="truncate block">
                          {line.service?.name ?? `Serviço #${line.serviceId}`}
                          {" — "}
                          {formatBudgetCurrency(line.price)}
                        </span>
                        {subs.length > 0 && (
                          <ul className="ml-3 text-xs text-gray-500 space-y-0.5">
                            {subs.map((sub) => (
                              <li key={`${line.serviceId}-${sub.subServiceId}`}>
                                • {sub.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                  {services.length > 3 && (
                    <li className="text-gray-400 italic">
                      +{services.length - 3} serviço(s)
                    </li>
                  )}
                  {services.length === 0 && budget.total != null && (
                    <li className="text-gray-400 italic">
                      Detalhes dos serviços não carregados
                    </li>
                  )}
                </ul>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => onDownloadPdf(budget)}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg transition-colors"
                  title="Baixar PDF"
                >
                  <Icons.Receipt sx={{ fontSize: 16 }} />
                  PDF
                </button>

                {hasPhone && (
                  <button
                    type="button"
                    onClick={() => onWhatsApp(budget)}
                    className={getWhatsAppButtonStyles("sm")}
                    title="Enviar por WhatsApp"
                    aria-label={`Enviar orçamento por WhatsApp para ${clientName}`}
                  >
                    <svg
                      className={getWhatsAppIconStyles("sm")}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path d={WHATSAPP_ICON_PATH} />
                    </svg>
                  </button>
                )}

                {isCreated && (
                  <>
                    <button
                      type="button"
                      onClick={() => onEdit(budget)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sky-600 hover:bg-sky-50 border border-sky-200 transition-colors"
                      title="Editar orçamento"
                      aria-label={`Editar orçamento #${budget.id}`}
                    >
                      <Icons.Edit sx={{ fontSize: 16 }} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onComplete(budget)}
                      className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs sm:text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
                      title="Marcar como concluído"
                    >
                      <Icons.Check sx={{ fontSize: 16 }} />
                      Concluir
                    </button>
                    <button
                      type="button"
                      onClick={() => onCancel(budget)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full text-red-600 hover:bg-red-50 border border-red-200 transition-colors"
                      title="Cancelar orçamento"
                      aria-label={`Cancelar orçamento #${budget.id}`}
                    >
                      <Icons.Delete sx={{ fontSize: 16 }} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
